export const VehicleTuning = {
  // Chassis dimensions (full, in meters) — sized to ~1/3 of a 2-lane road width.
  chassisWidth: 1.08,
  chassisHeight: 0.6,
  chassisLength: 2.52,
  chassisMass: 1400,
  /** Lower the visual mesh anchor by this amount (cosmetic). */
  visualYOffset: 0.0,

  // Wheels
  wheelRadius: 0.24,
  wheelWidth: 0.18,
  /** Horizontal offset from chassis center to wheel hub. */
  wheelTrack: 0.51,
  /** Half wheelbase: front wheels at +Z*frontAxle, rear at -Z*rearAxle. */
  frontAxle: 0.93,
  rearAxle: 0.93,
  /** Y offset (relative to chassis center) for the suspension anchor on the body. */
  wheelAnchorY: -0.15,

  // Suspension (Rapier units)
  suspensionRestLength: 0.27,
  suspensionTravel: 0.18,
  suspensionStiffness: 75,
  suspensionCompression: 1.7,
  suspensionRelaxation: 1.4,
  /** Max ride-height suspension force factor (in newtons). */
  maxSuspensionForce: 60000,

  // Friction / driving
  frictionSlip: 4.0,
  sideFrictionStiffness: 1.2,

  /** Engine force per driven wheel (N). */
  engineForce: 2400,
  /**
   * Total brake force in Newtons, applied as a linear impulse at the center of mass.
   * Applied at CoM rather than wheel contacts → zero pitch torque → no nose-flip.
   * For 1400 kg car, 12000 N ≈ 8.5 m/s² deceleration (stops from 100 km/h in ~3.3 s).
   */
  brakeForce: 12000,
  /** Handbrake force per rear wheel — applied via Rapier wheel brake so rear locks
   *  (still produces some rotational pitch, which is the point of handbrake skids). */
  handbrakeForce: 5000,
  /** How fast brake force ramps up toward target on key press (1/sec). ~12 ≈ 130 ms to 80%. */
  brakeRampRate: 12,
  /** Forward speed (m/s) above which brake is full; below 0, full reverse engine; blended in between. */
  brakeReverseBlendSpeed: 1.5,

  // Mass / center of mass
  /** Y offset of CoM from body center, in meters. Negative = below visual chassis center.
   *  Lower CoM reduces the lever arm of brake force around CoM, eliminating nose-flips. */
  centerOfMassY: -0.35,

  // Steering
  /** Max steering angle in radians at low speed. */
  maxSteer: 0.55,
  /** At/above this speed (m/s), steering is reduced to `minSteerAtHighSpeed`. */
  steerSpeedReference: 30,
  minSteerAtHighSpeed: 0.18,
  /** Steering rate (rad/sec) — how fast the wheel rotates toward target. */
  steerRate: 3.5,

  /**
   * Tree-felling behavior. All speed thresholds are in km/h (matches the speedometer the
   * player is feeling). A hit below `fellMinSpeedKmh` lets Rapier's solid-collider response
   * stop the car; above it, the tree topples and a piecewise-linear speed penalty is
   * applied: full `fellLossAtMinKmh` at the threshold, lerping to 0 at `fellLossZeroAtKmh`,
   * 0 above that (no penalty at speed — you barely feel it).
   *
   * Example with defaults below: at 15 km/h you lose 10 (slow to 5); at 37.5 km/h you
   * lose 5; at 60 km/h or above you lose 0.
   *
   * `cullRadius` bounds how many static tree colliders are active at once (trees beyond
   * this distance from the player are pure visuals — no perf cost).
   */
  treeFelling: {
    fellMinSpeedKmh: 15, // below this → tree stops the car (no felling)
    fellLossAtMinKmh: 10, // km/h subtracted when hitting at fellMinSpeedKmh
    fellLossZeroAtKmh: 60, // km/h above which there's zero speed penalty
    fallSeconds: 1.2,
    colliderRadius: 0.4, // m at scale=1; scaled per tree. A bit fatter than the visual trunk
    cullRadius: 80, // m — only trees within this radius of the player carry colliders
  },

  // Body damping — base values for road. Surface-dependent extra damping is added on top.
  /** Baseline linear damping (air/rolling resistance). */
  linearDamping: 0.05,
  /** Resists pitch/roll impulses from braking and bumpy terrain. */
  angularDamping: 1.8,

  /**
   * Surface-dependent handling. Per-surface knobs:
   *   - `throttleMul`        → scales engineForce (sets top speed; ≈ linear for this car).
   *   - `extraLinearDamping` → added on top of baseline `linearDamping`. Bites HARD — 0.05
   *     here roughly halves top speed on its own. Keep at 0 unless you specifically want
   *     coasting drag that drops top speed further.
   *   - `surfaceGripFloor`   → minimum REAR-wheel grip multiplier when the dynamic grip
   *     score has dropped to 0. Front wheels always keep full grip (so steering input
   *     still bites). On road this is `1.0` (rear never breaks loose); on grass it's a
   *     low value so a fully-stressed car drifts. The current grip is blended between
   *     this floor and 1.0 by the live `gripScore` (see `gripLossRate` etc below).
   *   - `gripStressMul`      → multiplier on the combined stress factors before they
   *     drive the grip score down. road: tiny (rarely break loose); grass: large
   *     (a couple of stressors stacked is enough).
   *   - `maxYawRateRadS`     → hard cap on body yaw angular velocity (|angvel.y|) in
   *     rad/s. Safety net for the rare full-grip-loss case — prevents the car spinning
   *     past a sane rate even when the rear is completely unsupported.
   *   - `extraAngularDamping` → added to baseline `angularDamping`. Shortens post-release
   *     drift tail; ~1.0–1.5 on grass gives a visible drift that dies out in ~1 s.
   *
   * Drivable roads (tarmac, residential, track, etc) are 'road'; OSM pedestrian classes
   * (footway, path, cycleway, etc) are 'path'; anything else is 'grass'.
   */
  surfaceHandling: {
    road:  { throttleMul: 1.0,  extraLinearDamping: 0.0, surfaceGripFloor: 1.0,  gripStressMul: 0.2, maxYawRateRadS: 2.5, extraAngularDamping: 0.0, slipDragMul: 0.3 },
    path:  { throttleMul: 0.75, extraLinearDamping: 0.0, surfaceGripFloor: 0.6,  gripStressMul: 0.8, maxYawRateRadS: 1.6, extraAngularDamping: 0.3, slipDragMul: 0.6 },
    grass: { throttleMul: 0.5,  extraLinearDamping: 0.0, surfaceGripFloor: 0.35, gripStressMul: 1.0, maxYawRateRadS: 1.2, extraAngularDamping: 0.5, slipDragMul: 1.0 },
  },

  /**
   * Dynamic grip model. Rear-wheel grip multiplier is a live `gripScore` (0..1) on the
   * Vehicle that lerps between `surfaceGripFloor` and 1. Each substep we compute three
   * "stress" factors and one speed gate:
   *   - lateral G (centripetal accel = speed · |yaw rate|) — hard cornering
   *   - longitudinal G (|d forwardSpeed / dt|, smoothed) — sharp accel/brake
   *   - steering rate (|d currentSteer / dt|, smoothed) — whipping the wheel
   *   - SPEED GATE: speedF raised to `speedExponent`. Gates the other three so that low
   *     speed → zero stress no matter how aggressive your inputs are, and stress only
   *     builds when you're actually going fast on a loose surface.
   *
   * Combined: `stress = avg(otherFactors) * speedGate * surfaceMul`.
   *
   * gripScore drifts toward `1 - stress` at `gripLossRate` (down) / `gripRecoverRate`
   * (up). Smoothing the two derivatives means a single deliberate counter-steer or brake
   * stab barely registers — only sustained chatter does. The speed gate is the key knob
   * for "casual off-road driving doesn't punish you, only high-speed mistakes do".
   */
  gripLossRate: 2.0,
  gripRecoverRate: 0.8,
  gripStress: {
    speedFull: 70, // m/s — speed at which the speed factor = 1 (~252 km/h)
    speedExponent: 1.5, // raises speedF; >1 makes low speeds contribute less. ~1.5 gives
    //                      mild-but-visible drift in the 100–200 km/h band on grass.
    lateralGFull: 12, // m/s² — centripetal accel at factor = 1 (~1.2 G)
    accelGFull: 12, // m/s² — longitudinal accel at factor = 1
    steerRateFull: 4, // rad/s — currentSteer change rate at factor = 1
    derivativeSmoothing: 0.05, // seconds — low-pass time constant for accelG and steerRate
  },

  /**
   * Slip drag — opposing impulse along the body's right axis, proportional to lateral
   * velocity SQUARED. Quadratic so normal cornering (small lateral velocity) is nearly
   * free, while a full sideways drift bleeds speed visibly. Surface multiplier scales it
   * (low on tarmac so handbrake feels arcade-fun, high on grass where sliding should hurt).
   * Roughly: at 5 m/s lateral on grass, ≈ 1 m/s² deceleration; at 15 m/s lateral, ≈ 8 m/s².
   */
  slipDrag: {
    coefficient: 50, // N per (m/s)² of lateral velocity
  },
} as const;

export type TreeFellingConfig = {
  fellMinSpeedKmh: number;
  fellLossAtMinKmh: number;
  fellLossZeroAtKmh: number;
  fallSeconds: number;
  colliderRadius: number;
  cullRadius: number;
};

export type SurfaceHandlingEntry = {
  throttleMul: number;
  extraLinearDamping: number;
  surfaceGripFloor: number;
  gripStressMul: number;
  maxYawRateRadS: number;
  extraAngularDamping: number;
  slipDragMul: number;
};

export type SurfaceHandlingTable = {
  road: SurfaceHandlingEntry;
  path: SurfaceHandlingEntry;
  grass: SurfaceHandlingEntry;
};
